import { useEffect, useState } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

import { apiUrl } from '@/lib/api-base';
import { isTokenExpired } from '@readyloop/sdk';
import { useAppModeStore } from '@/stores/appModeStore';
import { track, flush as flushTracker } from '@/lib/tracker';

/**
 * Action a user attempted while unauthenticated in SBAA mode. Stashed
 * when the auth-gate modal opens, replayed once the user signs in.
 * Persisted via the store's `partialize` so it survives the Google
 * OAuth full-page redirect.
 */
export type PendingIntent =
  | { type: 'chat_submit'; prompt: string; createdAt: number }
  | { type: 'card_prompt'; prompt: string; createdAt: number }
  | { type: 'voice'; createdAt: number };

/** Drop pending intents older than this to avoid replaying stale prompts. */
export const PENDING_INTENT_TTL_MS = 15 * 60 * 1000;

interface AuthState {
  /** JWT session token -- used as Bearer token for API calls */
  sessionToken: string | null;
  aurigaUid: string | null;
  /** Email address from the token exchange response */
  userEmail: string | null;
  /** Per-context conversation IDs: app slug (or '' for platform) -> conversation ID. */
  _conversationIds: Record<string, string>;
  debug: boolean;
  /** True once initialize() has run and session is resolved */
  hasHydrated: boolean;
  /** True while a flow conversation is being created (survives remounts) */
  creatingFlow: boolean;
  /**
   * Prompt to auto-send as the first user message of a specific
   * conversation once its runtime is ready. Used by welcome-card
   * clicks to skip the composer and inject the card's prompt.
   * Tagged with convId so we don't fire into a stale runtime.
   */
  pendingInitialPrompt: { convId: string; prompt: string } | null;

  /**
   * Intent the user attempted while unauthed. Replayed after OAuth
   * completes (see `usePendingIntentResume`). Persisted across the
   * full-page redirect.
   */
  pendingIntent: PendingIntent | null;

  /** Stripe subscription state */
  hasSubscription: boolean | null;
  subscriptionChecked: boolean;
  /** True once user has explicitly selected a plan (free or paid) server-side */
  planSelected: boolean | null;

  initialize: () => void;
  signInWithGoogle: () => Promise<void>;

  signOut: () => Promise<void>;
  /** Sync logout: clears local state immediately */
  logout: () => void;
  setConversationId: (id: string, slug?: string) => void;
  setDebug: (enabled: boolean) => void;
  setCreatingFlow: (v: boolean) => void;
  setPendingInitialPrompt: (
    v: { convId: string; prompt: string } | null,
  ) => void;
  setPendingIntent: (v: PendingIntent | null) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      sessionToken: null,
      aurigaUid: null,
      userEmail: null,
      _conversationIds: {},
      debug: false,
      hasHydrated: false,
      creatingFlow: false,
      pendingInitialPrompt: null,
      pendingIntent: null,
      hasSubscription: null,
      subscriptionChecked: false,
      planSelected: null,

      initialize: () => {
        const { sessionToken } = get();
        if (sessionToken && !isTokenExpired(sessionToken)) {
          // Valid token in store -- try refreshing it
          fetch(apiUrl('/api/v1/auth/refresh'), {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${sessionToken}`,
              'Content-Type': 'application/json',
            },
          })
            .then(async (resp) => {
              if (resp.ok) {
                const data = await resp.json();
                set({ sessionToken: data.token, hasHydrated: true });
              } else {
                // Token invalid -- clear state
                set({
                  sessionToken: null,
                  aurigaUid: null,
                  userEmail: null,
                  hasHydrated: true,
                });
              }
            })
            .catch(() => {
              // Network error -- keep existing token, mark hydrated
              set({ hasHydrated: true });
            });
        } else if (sessionToken) {
          // Expired token -- clear
          set({
            sessionToken: null,
            aurigaUid: null,
            userEmail: null,
            hasHydrated: true,
          });
        } else {
          set({ hasHydrated: true });
        }
      },

      signInWithGoogle: async () => {
        // Pass our origin so the backend redirects back here after OAuth
        const origin = encodeURIComponent(window.location.origin);
        // Carry the clickstream session id through the OAuth round-trip
        // as a belt-and-braces fallback for browsers that clear
        // localStorage between same-origin navigations. The backend
        // echoes it back on the frontend callback query string.
        let sidParam = '';
        try {
          const sid = window.localStorage.getItem('rl_cs_sid');
          if (sid) sidParam = `&client_sid=${encodeURIComponent(sid)}`;
        } catch {
          // localStorage blocked — skip the fallback
        }
        window.location.href = apiUrl(
          `/api/v1/auth/google?origin=${origin}${sidParam}`,
        );
      },

      signOut: async () => {
        set({
          sessionToken: null,
          aurigaUid: null,
          userEmail: null,
          _conversationIds: {},
          hasSubscription: null,
          subscriptionChecked: false,
          planSelected: null,
          pendingIntent: null,
        });
      },

      logout: () => {
        // Emit logout signal before state is cleared so the tracker
        // can still read uid.
        try {
          track({ type: 'logout' });
          flushTracker();
        } catch {
          // Never let analytics break logout
        }
        set({
          sessionToken: null,
          aurigaUid: null,
          userEmail: null,
          _conversationIds: {},
          hasSubscription: null,
          subscriptionChecked: false,
          planSelected: null,
          pendingIntent: null,
        });
      },

      setConversationId: (id, slug) => {
        // Explicit slug wins over the live store value. Callers that
        // capture the slug at the start of an async flow pass it
        // through so a later store change (e.g. appModeStore
        // re-initialize) can't persist the id under the wrong key.
        const key = slug ?? useAppModeStore.getState().slug ?? '';
        set((state) => ({
          _conversationIds: { ...state._conversationIds, [key]: id },
        }));
      },
      setDebug: (enabled) => set({ debug: enabled }),
      setCreatingFlow: (v) => set({ creatingFlow: v }),
      setPendingInitialPrompt: (v) => set({ pendingInitialPrompt: v }),
      setPendingIntent: (v) => set({ pendingIntent: v }),
    }),
    {
      name: 'readyloop-auth',
      partialize: (state) => ({
        aurigaUid: state.aurigaUid,
        sessionToken: state.sessionToken,
        userEmail: state.userEmail,
        _conversationIds: state._conversationIds,
        debug: state.debug,
        hasSubscription: state.hasSubscription,
        planSelected: state.planSelected,
        pendingIntent: state.pendingIntent,
      }),
      merge: (persisted, current) => {
        const merged = { ...current, ...(persisted as Partial<AuthState>) };
        // Drop stale pending intents so a long-dormant tab reload
        // doesn't replay a week-old prompt after the user signs in.
        const pi = merged.pendingIntent;
        if (pi && Date.now() - pi.createdAt > PENDING_INTENT_TTL_MS) {
          merged.pendingIntent = null;
        }
        return merged;
      },
    },
  ),
);

// Expose store on window for Playwright testing (dev + staging only)
if (
  typeof window !== 'undefined' &&
  (process.env.NODE_ENV === 'development' ||
    process.env.NEXT_PUBLIC_ENABLE_TEST_HOOKS === '1')
) {
  (window as any).__authStore = useAuthStore;
}

/**
 * Hook that returns true once the auth store has finished initializing
 * (session resolved + zustand rehydrated from localStorage).
 */
export function useHasHydrated() {
  const hasHydrated = useAuthStore((s) => s.hasHydrated);
  const [zustandHydrated, setZustandHydrated] = useState(false);

  useEffect(() => {
    if (useAuthStore.persist.hasHydrated()) {
      setZustandHydrated(true);
      return;
    }
    const unsub = useAuthStore.persist.onFinishHydration(() =>
      setZustandHydrated(true),
    );
    return unsub;
  }, []);

  return zustandHydrated && hasHydrated;
}
